import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, ExternalLink, Loader2, ScanLine, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useFacilityMutations, useFacilityOverview, useSiteLayout } from '@/hooks/useFacility'
import type { BuildingLink, LayoutBuilding, LinkKind, OverviewRoom, SitePlanApply, SitePlanProposal } from '@/api/facilityApi'
import { readSitePlanPicture } from '@/lib/sitePlanPicture'
import { plural, roomCount, sortFloors, type MapBox } from '@/lib/siteLayout'
import { BAR_META, BAR_ORDER, barCounts, type BarBucket } from '@/lib/roomStatus'
import { CampusMap, LINK_STYLE } from './CampusMap'

/** Re-reads the clock each minute so the bars follow hold times running out. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(id)
  }, [])
  return now
}

/**
 * Layout (Cleaning Visual p.5, "Layout #2", z8nrz7f731): the campus at a
 * glance — the site plan (an uploaded picture, or the buildings drawn,
 * arranged and connected by hand) and the Buildings list, each with its
 * rooms' status bar. Picking a building selects it here and shows its floors;
 * "Open in Overall View" goes the next level down.
 */
export function SiteLayoutDialog({
  open,
  onOpenChange,
  onSetUp,
  onOpenBuilding,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Opens Building set-up — offered when there is nothing to show yet. */
  onSetUp: () => void
  /** The Overall View, on this building. */
  onOpenBuilding: (buildingId: string) => void
}) {
  const now = useNow()
  const { data: layout, isLoading, error } = useSiteLayout(open)
  const { data: overview, isLoading: overviewLoading, error: overviewError } = useFacilityOverview(open)
  const roomsById = useMemo(() => new Map((overview?.rooms ?? []).map((r) => [r.id, r])), [overview])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const buildings = layout?.buildings ?? []
  const roomsOf = (floors: LayoutBuilding['floors']): OverviewRoom[] =>
    floors.flatMap((f) => f.rooms.map((r) => roomsById.get(r.id)).filter((r): r is OverviewRoom => !!r))
  const counts = new Map(buildings.map((b) => [b.id, barCounts(roomsOf(b.floors), now)]))
  const floorCounts = (floor: LayoutBuilding['floors'][number]) => barCounts(roomsOf([floor]), now)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-6xl'>
        <DialogHeader>
          <DialogTitle>Layout</DialogTitle>
          <DialogDescription>The campus at a glance. Pick a building to see its floors and rooms.</DialogDescription>
        </DialogHeader>

        {isLoading || overviewLoading ? (
          <div className='flex items-center gap-2 py-10 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' /> Loading the layout…
          </div>
        ) : error || overviewError ? (
          // No bars without the rooms' statuses: missing data must not read as "all clean".
          <p role='alert' className='py-6 text-sm text-destructive'>
            {(error ?? overviewError) instanceof Error ? (error ?? overviewError)!.message : 'Could not load the layout.'}
          </p>
        ) : buildings.length === 0 ? (
          <div className='rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground'>
            No buildings yet.{' '}
            <button type='button' onClick={onSetUp} className='font-medium text-primary hover:underline'>
              Set up a building
            </button>
            {layout && layout.unmappedRooms.length > 0 && (
              <p className='mt-1 text-xs'>{plural(layout.unmappedRooms.length, 'room')} waiting to be mapped.</p>
            )}
          </div>
        ) : (
          <div className='grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]'>
            <SitePlan
              buildings={buildings}
              links={layout?.links ?? []}
              counts={counts}
              selectedId={selectedId}
              onSelect={(id) => setSelectedId((cur) => (cur === id ? null : id))}
            />
            <BuildingsList
              buildings={buildings}
              counts={counts}
              floorCounts={floorCounts}
              selectedId={selectedId}
              onSelect={(id) => setSelectedId((cur) => (cur === id ? null : id))}
              onOpen={onOpenBuilding}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

type Counts = Map<string, Record<BarBucket, number>>

/** A building's code at a size its box can hold: the spec's big letter, smaller for longer codes (up to 8). */
const codeSize = (code: string, big: number) => (code.length <= 2 ? big : code.length <= 4 ? big * 0.66 : big * 0.5)

/** The rooms' status as one bar, a segment per colour in proportion to its rooms. */
function StatusBar({ counts, height }: { counts: Record<BarBucket, number> | undefined; height: number }) {
  const parts = BAR_ORDER.map((k) => ({ k, n: counts?.[k] ?? 0 })).filter((p) => p.n > 0)
  if (parts.length === 0) return <div className='rounded-sm bg-gray-200' style={{ height }} aria-hidden />
  return (
    <div className='flex gap-[3px]' style={{ height }} aria-hidden>
      {parts.map(({ k, n }) => (
        <span key={k} className='rounded-[1px]' style={{ flexGrow: n, flexBasis: 0, background: BAR_META[k].color }} />
      ))}
    </div>
  )
}

/** The counts under a bar: every colour with rooms in it but "clean", as in the spec. */
function BarLegend({ counts }: { counts: Record<BarBucket, number> | undefined }) {
  const items = BAR_ORDER.filter((k) => k !== 'clean' && (counts?.[k] ?? 0) > 0)
  if (items.length === 0) {
    const clean = counts?.clean ?? 0
    return (
      <p className='text-[11.5px] text-gray-500'>
        {clean === 0 ? 'No rooms mapped yet' : clean === 1 ? 'Its room is clean' : `All ${clean} rooms clean`}
      </p>
    )
  }
  return (
    <p className='flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-gray-600'>
      {items.map((k) => (
        <span key={k} className='inline-flex items-center gap-1'>
          <span className='h-[7px] w-[7px] rounded-full' style={{ background: BAR_META[k].color }} />
          {counts![k]} {BAR_META[k].label}
        </span>
      ))}
    </p>
  )
}

/** The building's most pressing line on the plan: what needs a clean now, else soon, else all clean. */
function AlertLine({ counts, rooms }: { counts: Record<BarBucket, number> | undefined; rooms: number }) {
  const k: BarBucket | null = counts?.attention ? 'attention' : counts?.soon ? 'soon' : null
  if (!k) {
    if (rooms === 0) return <span className='text-[11px] text-gray-500'>No rooms mapped</span>
    return (
      <span className='inline-flex items-center gap-1 text-[11px]' style={{ color: BAR_META.clean.color }}>
        <span className='h-[7px] w-[7px] rounded-full' style={{ background: BAR_META.clean.color }} />
        all clean
      </span>
    )
  }
  return (
    <span className='inline-flex items-center gap-1 text-[11px]' style={{ color: BAR_META[k].color }}>
      <span className='h-[7px] w-[7px] rounded-full' style={{ background: BAR_META[k].color }} />
      {counts![k]} {BAR_META[k].label}
    </span>
  )
}

/**
 * The left of the Layout: the drawn schematic, moved, resized and connected
 * directly. "Read from a picture" reads a simple sketch of the site (closed
 * outlines for buildings, lines between them) in the browser into a proposal,
 * reviewed and then applied to the schematic. The picture is not kept or shown.
 */
function SitePlan({
  buildings,
  links,
  counts,
  selectedId,
  onSelect,
}: {
  buildings: LayoutBuilding[]
  links: BuildingLink[]
  counts: Counts
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const mut = useFacilityMutations()
  const fileRef = useRef<HTMLInputElement>(null)
  const [reading, setReading] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [proposal, setProposal] = useState<SitePlanProposal | null>(null)
  const [linkId, setLinkId] = useState<string | null>(null)
  const link = links.find((l) => l.id === linkId) ?? null
  const nameOf = (id: string) => buildings.find((b) => b.id === id)?.name ?? '—'

  const attempt = async <T,>(fn: () => Promise<T>, fallback: string): Promise<T | undefined> => {
    setProblem(null)
    try {
      return await fn()
    } catch (e) {
      setProblem(e instanceof Error ? e.message : fallback)
      return undefined
    }
  }
  const readPicture = async (file: File) => {
    setNotice(null)
    setReading(true)
    const p = await attempt(async () => {
      const read = await readSitePlanPicture(file)
      if (read.buildings.length === 0) {
        throw new Error('No buildings found. Use a simple diagram: each building a rectangle with its name inside, on a plain background; connected buildings joined by a line or drawn touching.')
      }
      return mut.matchSitePlan.mutateAsync(read)
    }, 'The picture could not be read.')
    setReading(false)
    if (p) setProposal(p)
  }
  const place = (box: MapBox) =>
    void attempt(() => mut.updateBuilding.mutateAsync({ id: box.id, mapX: box.x, mapY: box.y, mapW: box.w, mapH: box.h }), 'That move was not saved.')
  // A new connection is a corridor; its editor opens at once to make it a bridge or tunnel.
  const connect = async (fromId: string, toId: string) => {
    const created = await attempt(() => mut.createLink.mutateAsync({ fromId, toId, kind: 'corridor' }), 'Those buildings were not connected.')
    if (created) setLinkId(created.id)
  }

  return (
    <section className='space-y-2'>
      <div className='flex flex-wrap items-center gap-2'>
        <h3 className='text-[13px] font-semibold text-[#1d2128]'>Site plan</h3>
        <input
          ref={fileRef}
          type='file'
          accept='image/png,image/jpeg,image/webp,image/svg+xml'
          className='hidden'
          aria-label='Site plan picture'
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (file) void readPicture(file)
          }}
        />
        {!proposal && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='ml-auto h-7 gap-1.5 text-xs'
            disabled={reading}
            onClick={() => fileRef.current?.click()}
          >
            {reading ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <ScanLine className='h-3.5 w-3.5' />}
            {reading ? 'Reading the picture…' : 'Read from a picture'}
          </Button>
        )}
      </div>
      {problem && (
        <p role='alert' className='text-[11.5px] text-destructive'>
          {problem}
        </p>
      )}
      {notice && <p className='text-[11.5px] text-[#1f8a3b]'>{notice}</p>}

      {proposal ? (
        <ReadingReview
          proposal={proposal}
          taken={buildings.map((b) => b.code)}
          onCancel={() => setProposal(null)}
          onApply={async (input) => {
            const out = await attempt(() => mut.applySitePlan.mutateAsync(input), 'The schematic was not updated.')
            if (!out) return
            setProposal(null)
            setNotice(
              `Schematic updated: ${plural(out.placed, 'building')} placed, ${plural(out.created, 'new building')}, ${plural(out.connected, 'connection')} added.`,
            )
          }}
        />
      ) : (
        <>
          <p className='text-[11px] text-gray-500'>Drag to move · corner to resize · blue dot to connect.</p>
          <CampusMap
            buildings={buildings}
            links={links}
            selectedId={selectedId}
            onSelect={onSelect}
            editable
            onPlace={place}
            onConnect={(a, b) => void connect(a, b)}
            selectedLinkId={linkId}
            onSelectLink={setLinkId}
            renderBuilding={(b, selected) => <BuildingTile building={b} counts={counts.get(b.id)} selected={selected} />}
          />
          {link && (
            <LinkEditor
              key={link.id}
              link={link}
              between={`${nameOf(link.fromId)} – ${nameOf(link.toId)}`}
              onSave={(input) => void attempt(() => mut.updateLink.mutateAsync({ id: link.id, ...input }), 'That connection was not saved.')}
              onRemove={() => {
                setLinkId(null)
                void attempt(() => mut.deleteLink.mutateAsync(link.id), 'That connection was not removed.')
              }}
              onClose={() => setLinkId(null)}
            />
          )}
        </>
      )}
    </section>
  )
}

type ReviewRow = SitePlanProposal['buildings'][number] & { keep: boolean }

/**
 * What was read off the picture, before anything is saved: the schematic it
 * would make, each building (a new one's code and name can be corrected) and
 * each connection (its kind can be changed), to keep or leave out.
 */
function ReadingReview({
  proposal,
  taken,
  onApply,
  onCancel,
}: {
  proposal: SitePlanProposal
  /** Codes of the buildings already here. */
  taken: string[]
  onApply: (input: SitePlanApply) => Promise<void>
  onCancel: () => void
}) {
  const [rows, setRows] = useState<ReviewRow[]>(() => proposal.buildings.map((b) => ({ ...b, keep: true })))
  // Connections by building index, so correcting a code keeps them attached.
  const [conns, setConns] = useState(() =>
    proposal.connections.map((c) => ({
      from: proposal.buildings.findIndex((b) => b.code === c.fromCode),
      to: proposal.buildings.findIndex((b) => b.code === c.toCode),
      kind: c.kind,
      label: c.label,
      exists: c.exists,
      keep: !c.exists,
    })),
  )
  const [busy, setBusy] = useState(false)
  const setRow = (i: number, patch: Partial<ReviewRow>) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)))
  const usable = (i: number) => i >= 0 && rows[i].keep
  const kept = conns.filter((c) => c.keep && !c.exists && usable(c.from) && usable(c.to))

  const codes = rows.filter((r) => r.keep).map((r) => r.code.trim().toLowerCase())
  const takenByOthers = new Set(taken.map((c) => c.toLowerCase()))
  const problem = rows.some((r) => r.keep && (!r.code.trim() || !r.name.trim()))
    ? 'Every building needs a code and a name.'
    : codes.some((c, i) => codes.indexOf(c) !== i) ||
        rows.some((r) => r.keep && !r.match && takenByOthers.has(r.code.trim().toLowerCase()))
      ? 'Two buildings have the same code.'
      : null

  const preview: LayoutBuilding[] = rows.flatMap((b, i) =>
    b.keep
      ? [
          {
            id: String(i),
            code: b.code,
            name: b.name,
            description: b.description,
            sortOrder: i,
            mapX: b.map.x,
            mapY: b.map.y,
            mapW: b.map.w,
            mapH: b.map.h,
            schematicUrl: null,
            schematicName: null,
            floors: [],
          },
        ]
      : [],
  )
  const previewLinks: BuildingLink[] = kept.map((c, i) => ({ id: `p${i}`, fromId: String(c.from), toId: String(c.to), kind: c.kind, label: c.label }))
  const codeOf = (i: number) => rows[i]?.code ?? '?'

  return (
    <div className='space-y-3 rounded-md border border-[#1a73e8]/30 bg-[#f3f7fe] p-3'>
      <p className='text-[12px] text-gray-700'>
        Read {plural(rows.length, 'building')} and {plural(conns.length, 'connection')}. Correct or untick anything wrong, then apply it to the
        schematic. Nothing is deleted.
      </p>
      <CampusMap buildings={preview} links={previewLinks} selectedId={null} onSelect={() => undefined} />
      <div className='grid gap-3 sm:grid-cols-2'>
        <ul className='space-y-1.5 text-[12px]' aria-label='Buildings read'>
          {rows.map((b, i) => (
            <li key={i} className='flex items-center gap-2'>
              <input
                type='checkbox'
                aria-label={`Keep ${b.name}`}
                checked={b.keep}
                onChange={() => setRow(i, { keep: !b.keep })}
                className='accent-[#1a73e8]'
              />
              {b.match ? (
                <>
                  <span className='font-semibold'>{b.code}</span>
                  <span className='min-w-0 flex-1 truncate'>{b.name}</span>
                </>
              ) : (
                <>
                  <Input
                    value={b.code}
                    onChange={(e) => setRow(i, { code: e.target.value.slice(0, 8) })}
                    aria-label='Code'
                    className='h-6 w-12 px-1.5 text-[12px] font-semibold'
                  />
                  <Input
                    value={b.name}
                    onChange={(e) => setRow(i, { name: e.target.value.slice(0, 120) })}
                    aria-label='Name'
                    className='h-6 min-w-0 flex-1 px-1.5 text-[12px]'
                  />
                </>
              )}
              <span className={cn('shrink-0 rounded px-1.5 text-[10.5px]', b.match ? 'bg-white text-gray-600' : 'bg-[#e9f6ed] text-[#1f8a3b]')}>
                {b.match ? 'moves' : 'new'}
              </span>
            </li>
          ))}
        </ul>
        <ul className='space-y-1.5 text-[12px]' aria-label='Connections read'>
          {conns.length === 0 && <li className='text-gray-500'>No connections read.</li>}
          {conns.map((c, i) => (
            <li key={i} className={cn('flex items-center gap-2', c.exists && 'text-gray-400')}>
              <input
                type='checkbox'
                aria-label={`Keep ${codeOf(c.from)} – ${codeOf(c.to)}`}
                checked={!c.exists && c.keep}
                disabled={c.exists}
                onChange={() => setConns((cs) => cs.map((x, k) => (k === i ? { ...x, keep: !x.keep } : x)))}
                className='accent-[#1a73e8]'
              />
              <span className='truncate'>
                {codeOf(c.from)} – {codeOf(c.to)}
              </span>
              {c.exists ? (
                <span className='ml-auto shrink-0 text-[10.5px]'>already there</span>
              ) : (
                <select
                  value={c.kind}
                  aria-label='Kind'
                  onChange={(e) => setConns((cs) => cs.map((x, k) => (k === i ? { ...x, kind: e.target.value as LinkKind } : x)))}
                  className='ml-auto h-6 rounded border border-gray-200 bg-white px-1 text-[11.5px]'
                >
                  {(Object.keys(LINK_STYLE) as LinkKind[]).map((k) => (
                    <option key={k} value={k}>
                      {LINK_STYLE[k].label}
                    </option>
                  ))}
                </select>
              )}
            </li>
          ))}
        </ul>
      </div>
      {problem && <p className='text-[11.5px] text-destructive'>{problem}</p>}
      <div className='flex justify-end gap-2'>
        <Button type='button' variant='ghost' size='sm' className='h-8 text-xs' onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          type='button'
          size='sm'
          className='h-8 text-xs'
          disabled={busy || !!problem || preview.length === 0}
          onClick={() => {
            setBusy(true)
            void onApply({
              buildings: rows
                .filter((b) => b.keep)
                .map((b) => ({
                  code: b.code.trim(),
                  name: b.name.trim(),
                  description: b.description,
                  ...(b.match ? { matchId: b.match.id } : {}),
                  mapX: b.map.x,
                  mapY: b.map.y,
                  mapW: b.map.w,
                  mapH: b.map.h,
                })),
              connections: kept.map((c) => ({ fromCode: codeOf(c.from).trim(), toCode: codeOf(c.to).trim(), kind: c.kind, label: c.label })),
            }).finally(() => setBusy(false))
          }}
        >
          {busy ? 'Applying…' : 'Apply to schematic'}
        </Button>
      </div>
    </div>
  )
}

/** A building on the drawn plan: code, what it is, rooms, its most pressing line and status bar. */
function BuildingTile({ building: b, counts, selected }: { building: LayoutBuilding; counts: Record<BarBucket, number> | undefined; selected: boolean }) {
  const rooms = roomCount(b.floors)
  // Drawn to its schematic: the plan fills the tile between a name line and the
  // status bar; the details are in the Buildings list beside the map.
  if (b.schematicUrl)
    return (
      <span className={cn('relative flex h-full flex-col justify-between', selected && 'text-[#1a3fae]')}>
        <span className='flex items-baseline gap-1 truncate bg-white/90 px-1.5 py-0.5 text-[11px] leading-tight'>
          <span className='font-bold'>{b.code}</span>
          <span className='truncate font-semibold'>{b.name}</span>
          <span className='shrink-0 text-gray-500'>· {plural(rooms, 'room')}</span>
        </span>
        <span className='bg-white/90 px-1.5 py-1'>
          <StatusBar counts={counts} height={5} />
        </span>
      </span>
    )
  return (
    <span className={cn('relative flex h-full flex-col justify-between p-2', selected && 'text-[#1a3fae]')}>
      <span className={cn('flex gap-1.5', b.code.length > 2 ? 'flex-col gap-0.5' : 'items-start')}>
        <span className='shrink-0 font-bold leading-none' style={{ fontSize: codeSize(b.code, 26) }}>
          {b.code}
        </span>
        <span className='min-w-0'>
          <span className='block truncate text-[12px] font-semibold'>{b.description || b.name}</span>
          <span className='block text-[10.5px] text-gray-500'>{plural(rooms, 'room')}</span>
          <AlertLine counts={counts} rooms={rooms} />
        </span>
      </span>
      <StatusBar counts={counts} height={6} />
    </span>
  )
}

/** A picked connection: its kind and label, or remove it. */
function LinkEditor({
  link,
  between,
  onSave,
  onRemove,
  onClose,
}: {
  link: BuildingLink
  between: string
  onSave: (input: { kind?: LinkKind; label?: string | null }) => void
  onRemove: () => void
  onClose: () => void
}) {
  const [label, setLabel] = useState(link.label ?? '')
  return (
    <div className='flex flex-wrap items-center gap-2 rounded-md border bg-white px-2.5 py-2 text-[11.5px]'>
      <span className='font-semibold text-gray-700'>{between}</span>
      <select
        value={link.kind}
        onChange={(e) => onSave({ kind: e.target.value as LinkKind })}
        className='h-7 rounded border bg-white px-1'
        aria-label='Connection kind'
      >
        {(Object.keys(LINK_STYLE) as LinkKind[]).map((k) => (
          <option key={k} value={k}>
            {LINK_STYLE[k].label}
          </option>
        ))}
      </select>
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={() => label.trim() !== (link.label ?? '') && onSave({ label: label.trim() || null })}
        onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
        placeholder='Label, e.g. Link corridor'
        className='h-7 w-44 text-[11.5px]'
        aria-label='Connection label'
        maxLength={60}
      />
      <Button type='button' variant='ghost' size='sm' className='ml-auto h-7 gap-1 text-xs text-destructive hover:text-destructive' onClick={onRemove}>
        <Trash2 className='h-3.5 w-3.5' /> Remove
      </Button>
      <Button type='button' variant='outline' size='sm' className='h-7 text-xs' onClick={onClose}>
        Done
      </Button>
    </div>
  )
}

function BuildingsList({
  buildings,
  counts,
  floorCounts,
  selectedId,
  onSelect,
  onOpen,
}: {
  buildings: LayoutBuilding[]
  counts: Counts
  floorCounts: (floor: LayoutBuilding['floors'][number]) => Record<BarBucket, number>
  selectedId: string | null
  onSelect: (id: string) => void
  onOpen: (id: string) => void
}) {
  return (
    <section>
      <h3 className='mb-2 text-[13px] font-semibold text-[#1d2128]'>Buildings</h3>
      <ul className='divide-y divide-gray-200'>
        {buildings.map((b) => {
          const selected = b.id === selectedId
          return (
            <li key={b.id} className={cn(selected && 'bg-[#f3f7fe]')}>
              <button
                type='button'
                onClick={() => onSelect(b.id)}
                aria-expanded={selected}
                className='flex w-full items-start gap-3 px-1 py-3 text-left hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1a73e8]'
              >
                <span
                  className={cn(
                    'flex h-9 min-w-9 shrink-0 items-center justify-center rounded-[3px] border-[1.5px] px-1 font-semibold',
                    selected ? 'border-[#1a73e8] bg-[#1a73e8] text-white' : 'border-[#2b2f36] text-[#1d2128]',
                  )}
                  style={{ fontSize: codeSize(b.code, 15) + (b.code.length > 2 ? 2 : 0) }}
                >
                  {b.code}
                </span>
                <span className='min-w-0 flex-1 space-y-1.5'>
                  <span className='block text-[13px] font-semibold text-[#1d2128]'>{b.name}</span>
                  <span className='block truncate text-[12px] text-gray-500'>
                    {[b.description, plural(b.floors.length, 'floor'), plural(roomCount(b.floors), 'room')].filter(Boolean).join(' · ')}
                  </span>
                  <StatusBar counts={counts.get(b.id)} height={8} />
                  <BarLegend counts={counts.get(b.id)} />
                </span>
                {selected ? <ChevronDown className='mt-2 h-4 w-4 shrink-0 text-[#1a73e8]' /> : <ChevronRight className='mt-2 h-4 w-4 shrink-0 text-gray-400' />}
              </button>
              {selected && (
                <div className='space-y-2 pb-3 pl-[52px] pr-1'>
                  {b.floors.length === 0 ? (
                    <p className='text-[11.5px] text-gray-500'>No floors yet.</p>
                  ) : (
                    <ul className='space-y-1.5' aria-label={`Floors of ${b.name}`}>
                      {sortFloors(b.floors).map((f) => (
                        <li key={f.id} className='rounded-md border bg-white px-2.5 py-1.5'>
                          <span className='flex items-baseline gap-2 text-[12px]'>
                            <span className='font-semibold text-[#1d2128]'>{f.name}</span>
                            {f.description && <span className='truncate text-gray-500'>{f.description}</span>}
                            <span className='ml-auto shrink-0 text-[11px] text-gray-500'>{plural(f.rooms.length, 'room')}</span>
                          </span>
                          <span className='mt-1 block'>
                            <StatusBar counts={floorCounts(f)} height={5} />
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button type='button' size='sm' className='h-7 gap-1.5 text-xs' onClick={() => onOpen(b.id)}>
                    <ExternalLink className='h-3.5 w-3.5' /> Open in Overall View
                  </Button>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
